import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { ChangeDirection, Start, Stop, Tick } from "./useTimer.actions";
import { useTimerReducer } from "./useTimer.reducer";
import { TimerDirection, TimerStatus } from "./useTimer.types";

type UseTimerProps = {
    autoStart?: boolean;
    autoRestart?: boolean;
    direction: TimerDirection;
    interval: number; // in milliseconds
    referenceTime: number; // in milliseconds
    onEnd?: () => void;
    onTick?: (time: number) => void;
};

export const useTimer = ({
    autoStart = false,
    autoRestart = false,
    direction,
    onEnd,
    onTick,
    interval,
    referenceTime
}: UseTimerProps) => {
    const startTime = useMemo(
        () => (direction === TimerDirection.Up ? 0 : referenceTime),
        [direction, referenceTime]
    );
    const [state, dispatch] = useReducer(useTimerReducer, {
        time: startTime,
        direction: direction,
        status: TimerStatus.Initial
    });
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const { time, direction: stateDirection, status } = state;

    const start = useCallback(() => {
        dispatch(Start(startTime));
    }, [startTime]);

    const stop = useCallback(() => {
        dispatch(Stop());
    }, []);

    useEffect(() => {
        if (status === TimerStatus.Running) {
            timerRef.current = setInterval(() => {
                dispatch(Tick(1 * interval));
            }, interval);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [interval, status]);

    useEffect(() => {
        if (onTick) {
            onTick(time);
        }
    }, [time, onTick]);

    useEffect(() => {
        if (direction !== stateDirection) {
            dispatch(ChangeDirection(direction));
        }
    }, [direction, stateDirection]);

    useEffect(() => {
        const endTime = direction === TimerDirection.Down ? 0 : referenceTime;
        if (status === TimerStatus.Running && time === endTime) {
            if (onEnd) {
                onEnd();
            }
            if (autoRestart) {
                start();
            } else {
                stop();
            }
        }
    }, [
        time,
        status,
        direction,
        referenceTime,
        onEnd,
        start,
        stop,
        autoRestart
    ]);

    useEffect(() => {
        if (autoStart) {
            start();
        }
    }, [autoStart, start]);

    return { start, stop, status };
};
